/**
 * Query templates per datastream kind.
 *
 * Not every stream is a time series. A raw or aggregated stream has
 * pre-aggregated SparseColumnar data at fixed resolutions, so a time window and a
 * matching resolution make sense. An event-feature stream has one value per event
 * — the gaps between them carry meaning and a fixed resolution does not apply. A
 * forecast stream holds simulated end-of-life samples, which is a distribution
 * rather than a series. Offering a time window for all three produces two plots
 * that are silently wrong.
 */

export type StreamKind = 'raw' | 'series' | 'event-feature' | 'event-feature-aggregation' | 'forecast' | 'other';

export interface Datastream {
    id: string;
    name: string;
    type: string;
    unit: string;
    aggregation: string;
    pipelineName: string;
    featureName: string;
    /**
     * For a virtual stream (`metadata.storageMode = 'source-datastreams'`, e.g. a
     * combined-channel-damage stream since combined_damage_v4): the streams whose
     * columnar holds its data. Empty when the stream stores its own data.
     */
    sourceIds: string[];
    /**
     * Admin-set display name of the one channel this stream is based on, when it
     * differs from the raw name the stream was named after (channels get renamed,
     * stream names do not follow). Null for streams not tied to exactly one channel
     * — event features across channels, forecasts — whose names are their own.
     */
    channelDisplay: { raw: string; display: string } | null;
    /**
     * The stream's own resolution in seconds, parsed from `samplingDefinition`
     * (`1s`, `10s`, `1m`, `10m`, `60m`). Each resolution is its own datastream, so
     * a `60m` stream holds 3600-second buckets and nothing else; null for raw or
     * event streams.
     */
    resolutionSeconds: number | null;
    /** Raw name of the stream's channel — the column name in raw matrix files. */
    channelName: string;
    /** Raw names of every channel the stream is linked to (usually one). */
    channelNames: string[];
    /** The channels as people know them: display name where set, raw name otherwise. */
    channelLabels: string[];
    /**
     * A raw stream (`bulk eq true`): its samples live in BulkDataLogs parquet files,
     * not in columnar buckets, and it has no fixed resolution.
     */
    raw: boolean;
    /**
     * For a forecast stream: the event type and channel it was built for, from
     * its own metadata. The RUL macros are addressed by these rather than by the
     * datastream id, which nobody knows.
     */
    forecastEventType: string;
    forecastChannel: string;
}

export interface EventType {
    id: string;
    name: string;
}

export interface EventWindowOptions {
    eventType: EventType;
    /** How many of the most recent events; 1 draws absolute time, more overlay. */
    count: number;
}

/**
 * The last n events of a type, with one or more streams' data inside each event
 * window. The streams must share a source: all raw (BulkDataLogs files) or all
 * columnar at one resolution — that is what the picker enforces.
 *
 * Rooted at /Datastreams so each stream brings its own pointer or files; the
 * events are the same for every stream. Events are read live (`/Events`, newest
 * first), so "the last event" stays the last event when the plot is refreshed
 * rather than freezing on the one it was created with. For columnar data the
 * `col_filter` binds the event row's `event_start`/`event_end`; for raw files the
 * same bounds go into `blob_select`'s `$time_from`/`$time_to`, which filter inside
 * the parquet read, one glob read per event over all of its files.
 *
 * One event is a time series on its own axis. Several are overlaid on seconds since
 * each event's start — the only layout in which they can be compared; laid end to
 * end they would repeat the "last hours" plot with gaps between.
 */
export function buildEventWindowQuery(streams: Datastream[], options: EventWindowOptions): string {
    if (!streams.length) {
        return '';
    }
    const count = Math.max(1, Math.min(50, Math.round(options.count)));
    const overlay = count > 1;
    const multi = streams.length > 1;
    const title = `${options.eventType.name} — ${overlay ? `last ${count} events` : 'last event'} — ${streamsTitle(streams)}`;
    const raw = streams.every((stream) => stream.raw);
    const ids = streams.map((stream) => stream.id).join(', ');
    const events = [
        `filter(ref_id(EventType) eq ${options.eventType.id} and parentId eq null)`,
        `/orderby(startTime desc)/top(${count})/select(id as event_id, startTime as event_start, endTime as event_end)`,
    ].join('');
    const read = raw
        ? [
              // Rooted at the events: each event's window bounds the raw files read
              // for it, and the file's stream comes along as `stream_id`.
              `/Events?resolve_blobs=false&$apply=${events}`,
              `/join(BulkDataLogs as __raw, filter(in(ref_id(Datastream), array(${ids}))`,
              ` and startTime le .event_end and endTime ge .event_start)/orderby(startTime asc)`,
              `/select(ref_id(Datastream) as stream_id, data))`,
              // One blob read per event over every file joined to it: the glob path
              // decodes each pointer in place with the event's bounds pushed into the
              // parquet read, so a file holding ten minutes around a thirty-second
              // event decodes only those seconds, and each file keeps its stream_id.
              // Needs pyst with in-place glob blob_select (2026-09-17 or later).
              `/blob_select($path('__raw/*/data'), $ts('Timestamp'), $time_from(event_start), $time_to(event_end))`,
              `/py_unwrap(__raw, $keep_other(true))`,
              `/py_unwrap(data, $keep_other(true))`,
              `/py_filter(time ge event_start and time le event_end)`,
              `/py_select(stream_id, event_id, event_start, event_end, time, value)`,
          ]
        : [
              // Rooted at the streams so each brings its newest pointer; the same
              // events are joined to every stream.
              `/Datastreams?$apply=filter(in(id, array(${ids})))`,
              `/join(Events as __events, ${events})`,
              `/join(SparseColumnar as __ptr, filter(ref_id(Datastream) eq .id`,
              ` and status eq 'active' and storagePointer ne null)/orderby(id desc)/top(1)`,
              `/select(storagePointer, resolutionSeconds))`,
              `/py_select(id as stream_id, __events as events, __ptr/0 as pointer)`,
              `/py_unwrap(events, $keep_other(true))`,
              `/py_select(stream_id, event_id, event_start, event_end, col_source(pointer)`,
              `/col_filter(time ge event_start and time le event_end)/col_select(time, value) as data)`,
              `/py_unwrap(data, $keep_other(true))`,
          ];
    // The legend entry: the stream when several are shown, the event when several
    // are overlaid, both when both.
    const streamLabel = streamLabelExpr(streams);
    const label = overlay && multi
        ? `concat(${streamLabel}, ' · ', cast(event_start, 'text'))`
        : overlay
            ? `cast(event_start, 'text')`
            : streamLabel;
    const grouped = overlay || multi ? `, $grouped(series_label)` : '';
    const overlaySplit = axisSplit(streams, 'seconds_since_start', grouped);
    const absoluteSplit = axisSplit(streams, 'time', grouped);
    const plot = overlay
        ? [
              `/py_enter((epochs_since(time) sub epochs_since(event_start)) as seconds_since_start, ${label} as series_label)`,
              `/py_orderby(event_start asc, time asc)`,
              `/py_plot(${overlaySplit.traces}`,
              `,$title(${quote(title)}),$xaxis('Seconds since event start')${axisArgs(overlaySplit)})`,
          ]
        : [
              `/py_enter(${label} as series_label)`,
              `/py_orderby(time asc)`,
              `/py_plot(${absoluteSplit.traces}`,
              `,$title(${quote(title)}),$xaxis('Time')${axisArgs(absoluteSplit)}${multi ? '' : ',$legend(false)'})`,
          ];
    return [...read, ...plot].join('');
}

/**
 * The legend name of each stream, resolved in the query from `stream_id`: an
 * `ifelse` chain over the chosen streams, so the plot shows people's names
 * (channel display name, aggregation) rather than datastream ids.
 */
function streamLabelExpr(streams: Datastream[]): string {
    if (streams.length === 1) {
        return quote(seriesLabel(streams[0]));
    }
    return streams.reduceRight(
        (rest, stream) => `ifelse(stream_id eq ${stream.id}, ${quote(seriesLabel(stream))}, ${rest})`,
        `cast(stream_id, 'text')`,
    );
}

/**
 * How one stream is named among several: the channel and aggregation when the
 * streams differ in either, the whole display name otherwise.
 */
export function seriesLabel(stream: Datastream): string {
    const channel = channelGroupOf(stream);
    const aggregation = aggregationLabel(stream);
    return aggregation && aggregation !== channel ? `${channel} · ${aggregation}` : displayStreamName(stream);
}

/** One title for several streams: the shared channel or the count. */
export function streamsTitle(streams: Datastream[]): string {
    if (streams.length === 1) {
        return displayStreamName(streams[0]);
    }
    const channels = unique(streams.map(channelGroupOf));
    const aggregations = unique(streams.map(aggregationLabel));
    if (channels.length === 1) {
        return `${channels[0]} — ${aggregations.join(', ')}`;
    }
    if (aggregations.length === 1) {
        return `${channels.join(', ')} — ${aggregations[0]}`;
    }
    return streams.length <= 3 ? streams.map(seriesLabel).join(', ') : `${streams.length} streams`;
}

function unique(values: string[]): string[] {
    return values.filter((value, index) => value && values.indexOf(value) === index);
}

/** The y axis for several streams: their unit when they agree, plain otherwise. */
function axisLabelFor(streams: Datastream[]): string {
    const units = unique(streams.map((stream) => present(stream.unit)));
    return units.length === 1 ? units[0] : 'Value';
}

/**
 * How several streams are laid out across the y axes.
 *
 * Streams in different units on one axis are unreadable: a strain of 40 and a
 * temperature of 40 are not the same quantity, and the larger range flattens the
 * smaller. So the first stream's unit keeps the left axis and every other unit
 * goes to the right.
 *
 * `$second_axis` is a per-trace flag and `$grouped` copies it to every group, so
 * one grouped trace cannot span both axes. Two traces over the same rows, each
 * narrowed by `$where`, is how pyst expresses it.
 */
interface AxisSplit {
    /** `$trace(...)` fragments, already comma-joined in emission order. */
    traces: string;
    /** Left axis label. */
    primary: string;
    /** Right axis label, or null when everything shares one axis. */
    secondary: string | null;
    /** The streams on the left axis — the only ones a threshold line can mean. */
    primaryStreams: Datastream[];
}

function axisSplit(streams: Datastream[], xField: string, grouped: string): AxisSplit {
    const trace = (extra: string) => `$trace(${xField} vs value as line${extra}${grouped})`;
    const units = unique(streams.map((stream) => present(stream.unit)));
    if (units.length <= 1 || streams.length < 2) {
        return {
            traces: trace(''),
            primary: axisLabelFor(streams),
            secondary: null,
            primaryStreams: streams,
        };
    }
    const primaryUnit = present(streams[0].unit);
    const left = streams.filter((stream) => present(stream.unit) === primaryUnit);
    const right = streams.filter((stream) => present(stream.unit) !== primaryUnit);
    const ids = (group: Datastream[]) => `in(stream_id, array(${group.map((s) => s.id).join(', ')}))`;
    return {
        traces: [
            trace(`, $where(${ids(left)})`),
            trace(`, $where(${ids(right)}), $second_axis(true)`),
        ].join(','),
        primary: primaryUnit || 'Value',
        secondary: unique(right.map((stream) => present(stream.unit))).join(', ') || 'Value',
        primaryStreams: left,
    };
}

/** `,$yaxis(left)` plus `,$yaxis2(right)` when a second axis is in play. */
function axisArgs(split: AxisSplit): string {
    const right = split.secondary ? `,$yaxis2(${quote(split.secondary)})` : '';
    return `,$yaxis(${quote(split.primary)})${right}`;
}

/**
 * The channel/sensor a stream belongs to, as the first level of the picker: the
 * channel's display name for a single-channel stream, the channels joined for a
 * combined one, the pipeline for streams with no channel at all.
 */
export function channelGroupOf(stream: Datastream): string {
    if (stream.channelLabels.length === 1) {
        return stream.channelLabels[0];
    }
    if (stream.channelLabels.length > 1) {
        return stream.channelLabels.join(' + ');
    }
    // Not reached from the picker, which drops channel-less streams; kept so the
    // label functions stay total for any stream handed to them.
    return present(stream.pipelineName) || 'No channel';
}

export interface Frequency {
    /** Stable key for grouping and compatibility checks. */
    key: string;
    label: string;
    /** Sort order in the picker: raw, then finer to coarser, then per-event, forecast. */
    order: number;
}

/**
 * The second level of the picker. Each columnar resolution is its own datastream,
 * raw is its own kind, and event features and forecasts have no resolution at all.
 */
export function frequencyOf(stream: Datastream): Frequency {
    switch (classifyStream(stream)) {
        case 'raw':
            return { key: 'raw', label: 'raw', order: 0 };
        case 'series':
            return stream.resolutionSeconds !== null
                ? { key: `${stream.resolutionSeconds}s`, label: resolutionLabel(stream.resolutionSeconds), order: stream.resolutionSeconds }
                : { key: 'series', label: 'series', order: 1e9 - 1 };
        case 'event-feature':
            return { key: 'event', label: 'per event', order: 1e9 };
        case 'event-feature-aggregation':
            return { key: 'event-aggregated', label: 'per event, aggregated', order: 1e9 + 1 };
        case 'forecast':
            return { key: 'forecast', label: 'forecast', order: 1e9 + 2 };
        default:
            return { key: 'other', label: 'other', order: 1e9 + 3 };
    }
}

/**
 * The third level of the picker: what the stream holds at that frequency. The
 * aggregation method for columnar streams, the feature (and its aggregation) for
 * event features; a raw stream is just "samples".
 */
export function aggregationLabel(stream: Datastream): string {
    if (stream.raw) {
        return 'samples';
    }
    if (classifyStream(stream) === 'forecast' && present(stream.forecastEventType)) {
        const variant = present(stream.aggregation);
        return variant && variant !== 'damage_forecast'
            ? `${stream.forecastEventType} (${variant.replace(/^damage_forecast_?/, '') || variant})`
            : stream.forecastEventType;
    }
    const parts = unique([present(stream.featureName), present(stream.aggregation)].filter(Boolean));
    return parts.join(' ') || displayStreamName(stream);
}

/** Kinds that can share one plot when they share a frequency. */
const MULTI_KINDS: StreamKind[] = ['raw', 'series'];

/** Whether streams of this kind can ever share a plot with each other. */
export function kindSupportsMulti(kind: StreamKind | null): boolean {
    return kind !== null && MULTI_KINDS.includes(kind);
}

/** Why two streams cannot share a plot, in a sentence, or null when they can. */
export function incompatibleReason(chosen: Datastream, candidate: Datastream): string | null {
    if (compatibleStreams(chosen, candidate)) {
        return null;
    }
    const chosenKind = classifyStream(chosen);
    const candidateKind = classifyStream(candidate);
    if (!kindSupportsMulti(candidateKind)) {
        return `${KIND_LABELS[candidateKind]} goes in a plot of its own.`;
    }
    if (!kindSupportsMulti(chosenKind)) {
        return `${KIND_LABELS[chosenKind]} goes in a plot of its own.`;
    }
    if (chosenKind !== candidateKind) {
        return `${KIND_LABELS[candidateKind]} and ${KIND_LABELS[chosenKind]} are read from different places, so they cannot share a plot.`;
    }
    return `${frequencyOf(candidate).label} cannot share a plot with ${frequencyOf(chosen).label}: one window reads one resolution.`;
}

/**
 * Whether two streams can be drawn in one plot: same kind and same frequency, so a
 * single window and a single read path fits both. Event features, forecasts and
 * unsupported streams are one per plot.
 */
export function compatibleStreams(a: Datastream, b: Datastream): boolean {
    const kind = classifyStream(a);
    return MULTI_KINDS.includes(kind) && kind === classifyStream(b) && frequencyOf(a).key === frequencyOf(b).key;
}

/** `samplingDefinition` → seconds; null when it is not a fixed resolution. */
export function parseResolutionSeconds(samplingDefinition: string): number | null {
    const match = samplingDefinition.trim().toLowerCase().match(/^(\d+(?:\.\d+)?)\s*([a-z]+)$/);
    if (!match) {
        return null;
    }
    const value = Number(match[1]);
    const unit = DURATION_UNIT_SECONDS[match[2]];
    if (!Number.isFinite(value) || value <= 0 || unit === undefined) {
        return null;
    }
    const seconds = value * unit;
    return Number.isInteger(seconds) ? seconds : null;
}

/**
 * Windows worth offering for a stream of a fixed resolution: long enough that
 * the buckets are not a handful, short enough that plotly is not asked to draw
 * hundreds of thousands of points. Every preset reads the stream at its own
 * resolution — that is the only one it has.
 */
export function windowsForResolution(resolutionSeconds: number): WindowPreset[] {
    const label = resolutionLabel(resolutionSeconds);
    const at = (name: string, seconds: number): WindowPreset => ({
        label: name,
        seconds,
        resolutionSeconds,
        resolutionLabel: label,
    });
    if (resolutionSeconds <= 1) {
        return [at('Last hour', HOUR), at('Last 6 hours', 6 * HOUR), at('Last 24 hours', DAY)];
    }
    if (resolutionSeconds <= 10) {
        return [at('Last 6 hours', 6 * HOUR), at('Last 24 hours', DAY), at('Last 7 days', 7 * DAY)];
    }
    if (resolutionSeconds <= 60) {
        return [at('Last 24 hours', DAY), at('Last 7 days', 7 * DAY), at('Last 30 days', 30 * DAY)];
    }
    if (resolutionSeconds <= 600) {
        return [at('Last 7 days', 7 * DAY), at('Last 30 days', 30 * DAY), at('Last 90 days', 90 * DAY)];
    }
    return [
        at('Last 30 days', 30 * DAY),
        at('Last 90 days', 90 * DAY),
        at('Last year', 365 * DAY),
        at('Last 5 years', 5 * 365 * DAY),
    ];
}

function resolutionLabel(seconds: number): string {
    if (seconds % 3600 === 0) {
        return seconds === 3600 ? '60 min' : `${seconds / 3600} h`;
    }
    if (seconds % 60 === 0) {
        return `${seconds / 60} min`;
    }
    return `${seconds}s`;
}

/**
 * A warning threshold on a value plot: the line, the zone past it shaded, and the
 * samples past it drawn as red markers so the exceedances are the first thing seen.
 */
export interface ThresholdOptions {
    value: number;
    /** Which side of the line is the bad one. */
    direction: 'above' | 'below';
}

/**
 * A threshold's marker trace and line. `$line` has no axis selector and always
 * draws on the primary axis, so when the plot has two axes the threshold is
 * narrowed to the streams on the left one and its label names that unit — a line
 * at 50 means nothing to a series measured in something else.
 */
function thresholdPlotArgs(
    threshold: ThresholdOptions | null | undefined,
    xField: string,
    axis?: { streams: Datastream[]; unit: string } | null,
): string[] {
    if (!threshold || !Number.isFinite(threshold.value)) {
        return [];
    }
    const value = String(threshold.value);
    const above = threshold.direction === 'above';
    const onlyPrimary = axis
        ? ` and in(stream_id, array(${axis.streams.map((stream) => stream.id).join(', ')}))`
        : '';
    const label = axis ? `warning threshold · ${axis.unit}` : 'warning threshold';
    return [
        `,$trace(${xField} vs value as scatter, $where(value ${above ? 'gt' : 'lt'} ${value}${onlyPrimary})`,
        `, $color('#b3261e'), $name(${quote(`${above ? 'above' : 'below'} threshold`)}))`,
        `,$line(${value} as horizontal, $color('#b3261e'), ${above ? '$shade_after' : '$shade_before'}(true)`,
        `, $shade_color('#f7d9d9'), $dash('dash'), $label(${quote(label)}))`,
    ];
}


/**
 * Where an event-feature stream's rows actually live. A virtual stream writes
 * nothing under its own id — pyst's damage forecast reads its sources by
 * `in(ref_id(Datastream), sourceDatastreamIdArray)` — so a query has to do the same
 * or it finds no rows.
 */
export function eventSourceIds(stream: Datastream): string[] {
    return stream.sourceIds.length ? stream.sourceIds : [stream.id];
}

/**
 * Where a clicked event point should open. The plot is a standalone document, so the
 * link has to be absolute — a relative path would resolve against the blob URL the
 * frame is served from, not the dashboard.
 */
export interface EventLinkOptions {
    dashboardOrigin?: string;
}

function eventLinkUrl(options?: EventLinkOptions): string | null {
    const origin = (options?.dashboardOrigin ?? '').trim().replace(/\/+$/, '');
    return origin ? `${origin}/events/{}` : null;
}

export interface WindowPreset {
    label: string;
    /** Window length in seconds. */
    seconds: number;
    resolutionSeconds: number;
    resolutionLabel: string;
}

const HOUR = 3600;
const DAY = 86400;

/**
 * Relative start of a window.
 *
 * Resolved by pyst per request, so the window stays relative and an embedded plot
 * keeps showing the last N days instead of freezing on the window it was created
 * with. The same expression works inside a columnar filter and outside one.
 */
function windowStart(preset: WindowPreset): string {
    return `now() sub interval('${intervalLiteral(preset.seconds)}')`;
}

/**
 * A duration as the largest whole unit that divides it, so a reader of the query
 * sees `30 days` rather than `2592000 seconds` and does no arithmetic.
 *
 * Days are the coarsest unit used: postgres months and years are calendar-aware
 * (a month is not 30 days), so naming them would change what the query means.
 */
export function intervalLiteral(seconds: number): string {
    const whole = Math.max(1, Math.round(seconds));
    for (const [unit, size] of [['day', 86400], ['hour', 3600], ['minute', 60]] as const) {
        if (whole % size === 0) {
            const count = whole / size;
            return `${count} ${unit}${count === 1 ? '' : 's'}`;
        }
    }
    return `${whole} second${whole === 1 ? '' : 's'}`;
}

/**
 * Resolutions the backend actually stores (see the dashboard's sparse_backend.ts:
 * 1s/10s/1min/10min/60min). Asking for a finer one than the window warrants returns
 * far more points than a plot can draw.
 */
export const WINDOW_PRESETS: WindowPreset[] = [
    { label: 'Last hour', seconds: HOUR, resolutionSeconds: 1, resolutionLabel: '1s' },
    { label: 'Last 6 hours', seconds: 6 * HOUR, resolutionSeconds: 10, resolutionLabel: '10s' },
    { label: 'Last 24 hours', seconds: DAY, resolutionSeconds: 60, resolutionLabel: '1 min' },
    { label: 'Last 7 days', seconds: 7 * DAY, resolutionSeconds: 600, resolutionLabel: '10 min' },
    { label: 'Last 30 days', seconds: 30 * DAY, resolutionSeconds: 3600, resolutionLabel: '60 min' },
    { label: 'Last 90 days', seconds: 90 * DAY, resolutionSeconds: 3600, resolutionLabel: '60 min' },
];

/**
 * Windows for raw streams: short, since a raw file may hold hundreds of samples a
 * second and everything overlapping the window is decoded before it is trimmed.
 */
export const RAW_WINDOWS: WindowPreset[] = [
    { label: 'Last 10 minutes', seconds: 600, resolutionSeconds: 0, resolutionLabel: 'raw' },
    { label: 'Last hour', seconds: HOUR, resolutionSeconds: 0, resolutionLabel: 'raw' },
    { label: 'Last 6 hours', seconds: 6 * HOUR, resolutionSeconds: 0, resolutionLabel: 'raw' },
];

/** Windows for event-feature streams: a range, with no resolution to choose. */
export const EVENT_WINDOWS: WindowPreset[] = [
    { label: 'Last 7 days', seconds: 7 * DAY, resolutionSeconds: 0, resolutionLabel: 'per event' },
    { label: 'Last 30 days', seconds: 30 * DAY, resolutionSeconds: 0, resolutionLabel: 'per event' },
    { label: 'Last 90 days', seconds: 90 * DAY, resolutionSeconds: 0, resolutionLabel: 'per event' },
    { label: 'Last year', seconds: 365 * DAY, resolutionSeconds: 0, resolutionLabel: 'per event' },
];

export function classifyStream(stream: { type: string; aggregation: string; raw?: boolean }): StreamKind {
    const type = stream.type.trim().toLowerCase();
    const aggregation = stream.aggregation.trim().toLowerCase();

    if (stream.raw) {
        return 'raw';
    }
    if (type === 'event-feature-forecast' || aggregation.startsWith('damage_forecast')) {
        return 'forecast';
    }
    if (type === 'event-feature-aggregation') {
        return 'event-feature-aggregation';
    }
    if (type === 'event-feature') {
        return 'event-feature';
    }
    if (type === 'raw' || type === 'aggregation' || type === '') {
        return 'series';
    }
    return 'other';
}

export const KIND_LABELS: Record<StreamKind, string> = {
    'raw': 'raw samples',
    'series': 'time series',
    'event-feature': 'event feature',
    'event-feature-aggregation': 'event feature (aggregated)',
    'forecast': 'forecast',
    'other': 'unsupported',
};

export const KIND_NOTES: Record<StreamKind, string> = {
    'raw': 'Every sample as logged, read from the raw files — keep the window short.',
    'series': 'Pre-aggregated samples at the stream\'s own resolution — pick how far back to read.',
    'event-feature': 'One value per event, so there is no resolution to choose; pick how far back to read.',
    'event-feature-aggregation': 'Aggregated per event and stored directly rather than in blobs; pick how far back to read.',
    'forecast': 'Holds simulated end-of-life samples, so the plot is a distribution rather than a series.',
    'other': 'No template for this stream type yet — write the query by hand with "+ Add plot".',
};

function quote(value: string): string {
    return `'${value.replace(/'/g, "''")}'`;
}

/** Absent values arrive as the literal string "null", which must not reach a label. */
function present(value: string): string {
    const trimmed = value.trim();
    return !trimmed || trimmed.toLowerCase() === 'null' ? '' : trimmed;
}

function axisLabel(stream: Datastream): string {
    return present(stream.unit) || 'Value';
}

/**
 * The stream name as a person would say it: for a stream based on one renamed
 * channel, the raw channel name in it is swapped for the channel's display name.
 * Whole-word only (`CH1` must not touch `CH12`); if the raw name does not occur,
 * the display name is appended instead so it is not lost. Streams without a
 * channel display keep their name exactly. Used for plot names and titles only —
 * the datastream itself is never renamed.
 */
export function displayStreamName(stream: Datastream): string {
    const name = present(stream.name);
    const channel = stream.channelDisplay;
    if (!channel) {
        return name;
    }
    const escaped = channel.raw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const wholeWord = new RegExp(`(^|[^\\p{L}\\p{N}_])${escaped}(?=$|[^\\p{L}\\p{N}_])`, 'gu');
    if (!wholeWord.test(name)) {
        return `${name} (${channel.display})`;
    }
    return name.replace(wholeWord, `$1${channel.display}`);
}

/** The stream name usually already ends with the feature name; do not repeat it. */
function plotTitle(stream: Datastream): string {
    const name = displayStreamName(stream);
    const feature = present(stream.featureName);
    if (!feature || name.toLowerCase().includes(feature.toLowerCase())) {
        return name;
    }
    return `${name} — ${feature}`;
}

/**
 * SparseColumnar time series, following the dashboard's query_builder.ts: pick the
 * pre-aggregated stream at the wanted resolution, take its newest storage pointer,
 * then read the window out of the blob. The window is relative so an embedded plot
 * stays current instead of freezing when it was created.
 */
export function buildSeriesQuery(
    stream: Datastream,
    preset: WindowPreset,
    threshold?: ThresholdOptions | null,
): string {
    const from = windowStart(preset);
    const aggregation = present(stream.aggregation);
    const title = aggregation ? `${displayStreamName(stream)} (${aggregation})` : displayStreamName(stream);
    const thresholdArgs = thresholdPlotArgs(threshold, 'time');

    return [
        // The datastream pins the row (SparseColumnar is unique per datastream), so
        // its resolution is not a predicate; top(1) stays as a guard.
        `/SparseColumnar?$apply=filter(points(Datastream,${stream.id})`,
        ` and status eq 'active' and storagePointer ne null)`,
        `/orderby(id desc)/top(1)`,
        `/py_select(col_source(storagePointer)/col_filter(time ge ${from})/col_select(time, value) as data)`,
        `/py_unwrap(data)/py_select(time, value)/py_orderby(time asc)`,
        `/py_plot($trace(time vs value as line)`,
        ...thresholdArgs,
        `,$title(${quote(title)}),$xaxis('Time'),$yaxis(${quote(axisLabel(stream))})`,
        // With a threshold the legend explains the red line and markers.
        thresholdArgs.length ? `)` : `,$legend(false))`,
    ].join('');
}

/**
 * Several columnar streams at one resolution in one plot, one line each. Rooted at
 * /Datastreams so every stream brings its own newest storage pointer; the lines are
 * named in the query from the stream id so the legend reads as people name things.
 */
export function buildMultiSeriesQuery(
    streams: Datastream[],
    preset: WindowPreset,
    threshold?: ThresholdOptions | null,
): string {
    if (streams.length === 1) {
        return buildSeriesQuery(streams[0], preset, threshold);
    }
    const from = windowStart(preset);
    const split = axisSplit(streams, 'time', ', $grouped(series_label)');
    const thresholdArgs = thresholdPlotArgs(
        threshold,
        'time',
        split.secondary ? { streams: split.primaryStreams, unit: split.primary } : null,
    );
    return [
        `/Datastreams?$apply=`,
        `filter(in(id, array(${streams.map((stream) => stream.id).join(', ')})))`,
        // No resolution predicate: SparseColumnar is unique per datastream, so
        // pinning the stream already pins its row, and a resolution that
        // disagreed with the stored value would turn a certain hit into a miss.
        // resolutionSeconds rides along so the columnar reader can size its
        // export chunks from it rather than falling back to six hours.
        `/join(SparseColumnar as __ptr, filter(ref_id(Datastream) eq .id`,
        ` and status eq 'active' and storagePointer ne null)/orderby(id desc)/top(1)`,
        `/select(storagePointer, resolutionSeconds))`,
        `/py_select(id as stream_id, col_source(__ptr/0)/col_filter(time ge ${from})/col_select(time, value) as data)`,
        `/py_unwrap(data, $keep_other(true))`,
        `/py_enter(${streamLabelExpr(streams)} as series_label)`,
        // Label first: plotly orders the legend by first appearance, so ordering
        // by time alone would leave it at the mercy of which stream logged first.
        `/py_orderby(series_label asc, time asc)`,
        `/py_plot(${split.traces}`,
        ...thresholdArgs,
        `,$title(${quote(streamsTitle(streams))}),$xaxis('Time')${axisArgs(split)})`,
    ].join('');
}

/**
 * Raw samples over a window, from BulkDataLogs the way the dashboard's raw view
 * reads them: the files overlapping the window, decoded with `Timestamp` as the
 * time column (the pointer already names the channel's column, which decodes as
 * `value`). The window start goes into `$time_from`, which blob_select evaluates
 * per row, so the parquet reader skips row groups before it instead of decoding
 * the whole file and trimming in memory; the `py_filter` after it only makes the
 * bound exact. The entity filter on `endTime` keeps files outside the window
 * unread altogether.
 */
export function buildRawQuery(
    streams: Datastream[],
    preset: WindowPreset,
    threshold?: ThresholdOptions | null,
): string {
    if (!streams.length) {
        return '';
    }
    const from = windowStart(preset);
    const multi = streams.length > 1;
    const split = axisSplit(streams, 'time', multi ? ', $grouped(series_label)' : '');
    const thresholdArgs = thresholdPlotArgs(
        threshold,
        'time',
        split.secondary ? { streams: split.primaryStreams, unit: split.primary } : null,
    );
    const read = multi
        ? [
              // The file's stream comes along beside the pointer, so the decoded
              // rows can be labelled after the unwrap.
              `/BulkDataLogs?resolve_blobs=false&$apply=`,
              `filter(in(ref_id(Datastream), array(${streams.map((stream) => stream.id).join(', ')})) and endTime ge ${from})`,
              `/orderby(startTime asc)/select(ref_id(Datastream) as stream_id, data)`,
              `/blob_select($path('data'), $ts('Timestamp'), $time_from(${from}))`,
              `/py_unwrap(data, $keep_other(true))`,
              `/py_filter(time ge ${from})`,
              `/py_enter(${streamLabelExpr(streams)} as series_label)`,
              // stream_id stays on the row: a two-axis plot narrows each trace by it.
              `/py_select(stream_id, series_label, time, value)`,
              `/py_orderby(series_label asc, time asc)`,
          ]
        : [
              `/BulkDataLogs?resolve_blobs=false&$apply=`,
              `filter(points(Datastream, ${streams[0].id}) and endTime ge ${from})/orderby(startTime asc)/select(data)`,
              `/blob_select($path('data'), $ts('Timestamp'), $time_from(${from}))`,
              `/py_unwrap(data)`,
              `/py_filter(time ge ${from})`,
              `/py_select(time, value)`,
          ];
    return [
        ...read,
        ...(multi ? [] : [`/py_orderby(time asc)`]),
        `/py_plot(${split.traces}`,
        ...thresholdArgs,
        `,$title(${quote(streamsTitle(streams))}),$xaxis('Time')${axisArgs(split)}`,
        multi || thresholdArgs.length ? `)` : `,$legend(false))`,
    ].join('');
}

/**
 * Raw per-event values of an event-feature stream.
 *
 * Rooted at /EventFeatureColumnar, not /Datastreams: `col_source` resolves against
 * the request entity, and only an entity marked as columnar storage is accepted —
 * going through /Datastreams fails with "not marked as Columnar storage". This
 * follows the event-feature branch of the dashboard's query_builder.ts.
 *
 * Markers rather than a line: the points are discrete events, and joining them
 * would imply a continuity that is not there.
 */
export function buildEventFeatureQuery(
    stream: Datastream,
    preset: WindowPreset,
    options?: EventLinkOptions,
    threshold?: ThresholdOptions | null,
): string {
    const from = windowStart(preset);
    const title = plotTitle(stream);
    const link = eventLinkUrl(options);
    const thresholdArgs = thresholdPlotArgs(threshold, 'time');
    // event_id is a real column on the event-feature columnar table, so each point
    // can carry the event it came from and open it when clicked.
    const clickable = link
        ? [`,$customdata(event_id)`, `)`, `,$onclick(${quote(link)})`]
        : [`)`];

    return [
        `/EventFeatureColumnar?resolve_blobs=false&$apply=`,
        `filter(in(ref_id(Datastream), array(${eventSourceIds(stream).join(', ')})) and status eq 'active'`,
        ` and storagePointer ne null and not(contains(storagePointer,'event_id=')))`,
        `/select(storagePointer)`,
        `/py_select(col_source(storagePointer)/col_filter(time ge ${from})`,
        `/col_select(time, value${link ? ', event_id' : ''}) as data)`,
        `/py_unwrap(data)/py_orderby(time asc)`,
        `/py_select(time, value${link ? ', event_id' : ''})`,
        `/py_plot($trace(time vs value as scatter`,
        ...clickable,
        ...thresholdArgs,
        `,$title(${quote(title)}),$xaxis('Event time'),$yaxis(${quote(axisLabel(stream))})`,
        thresholdArgs.length ? `)` : `,$legend(false))`,
    ].join('');
}

/**
 * Event-feature *aggregation* streams are not blob-backed: the dashboard reads them
 * from MutableSparseDataLogs directly, with a plain time filter.
 */
export function buildEventFeatureAggregationQuery(
    stream: Datastream,
    preset: WindowPreset,
    threshold?: ThresholdOptions | null,
): string {
    const from = windowStart(preset);
    const title = plotTitle(stream);
    const thresholdArgs = thresholdPlotArgs(threshold, 'time');

    return [
        `/MutableSparseDataLogs?$apply=`,
        `filter(in(ref_id(Datastream), array(${stream.id})) and time ge ${from})`,
        `/select(time, value)/orderby(time asc)`,
        `/py_select(time, value)`,
        `/py_plot($trace(time vs value as scatter)`,
        ...thresholdArgs,
        `,$title(${quote(title)}),$xaxis('Event time'),$yaxis(${quote(axisLabel(stream))})`,
        thresholdArgs.length ? `)` : `,$legend(false))`,
    ].join('');
}

/** The forecast views pyst renders server-side; each is its own macro. */
export type ForecastView = 'card' | 'survival' | 'histogram-years' | 'histogram-dates';

export const FORECAST_VIEWS: { view: ForecastView; label: string; note: string }[] = [
    { view: 'card', label: 'RUL card', note: 'Distribution of predicted end of life as a violin, with percentiles.' },
    { view: 'survival', label: 'Survival curve', note: 'Share of simulated futures still within the limit on each date.' },
    { view: 'histogram-years', label: 'Histogram — years remaining', note: 'Predicted end-of-life outcomes binned by years from now.' },
    { view: 'histogram-dates', label: 'Histogram — dates', note: 'The same outcomes binned by calendar date.' },
];

export interface ForecastOptions {
    view: ForecastView;
    /** Configured warning threshold in years from now; marked on every view. */
    criticalYears?: number | null;
}

/**
 * The forecast macros own the forecast-specific query; the builder only picks the
 * view and passes the threshold. No title: the macros title themselves with the
 * channel's display name.
 *
 * The stream is named by what people know — the event type and the channel — and
 * the macro finds the forecast stream from those. Only a stream whose metadata
 * lacks them falls back to the datastream id.
 */
export function buildForecastQuery(stream: Datastream, options: ForecastOptions = { view: 'card' }): string {
    const critical = options.criticalYears !== null && options.criticalYears !== undefined && Number.isFinite(options.criticalYears)
        ? `,$critical_years(${options.criticalYears})`
        : '';
    const method = present(stream.aggregation);
    const selector = present(stream.forecastEventType) && present(stream.forecastChannel)
        ? `$event_type(${quote(stream.forecastEventType)}),$channel(${quote(stream.forecastChannel)})`
              + (method && method !== 'damage_forecast' ? `,$method(${method})` : '')
        : `$datastream_id(${stream.id})`;
    switch (options.view) {
        case 'survival':
            return `/~rul_survival(${selector}${critical})`;
        case 'histogram-years':
            return `/~rul_histogram(${selector},$scale(years)${critical})`;
        case 'histogram-dates':
            return `/~rul_histogram(${selector},$scale(absolute)${critical})`;
        default:
            return `/~rul_card(${selector}${critical})`;
    }
}

/**
 * Duration units to seconds, so `60m`, `1h`, `1 hour` and `3600s` are one token.
 * Streams are named with whichever form the pipeline used; people type another.
 */
const DURATION_UNIT_SECONDS: Record<string, number> = {
    s: 1, sec: 1, secs: 1, second: 1, seconds: 1,
    m: 60, min: 60, mins: 60, minute: 60, minutes: 60,
    h: 3600, hr: 3600, hrs: 3600, hour: 3600, hours: 3600,
    d: 86400, day: 86400, days: 86400,
    w: 604800, wk: 604800, week: 604800, weeks: 604800,
};

/** Aggregation spellings folded to one token. */
const AGGREGATION_SYNONYMS: Record<string, string> = {
    minimum: 'min', maximum: 'max',
    avg: 'mean', average: 'mean',
    std: 'std', stddev: 'std', stdev: 'std', deviation: 'std',
    med: 'median',
    cnt: 'count',
};

/**
 * Search text normalised for matching: lower case, `_`, `-`, `.` and `/` read as
 * word separators, durations folded to seconds (`60m` = `1h` = `1 hour` → `3600s`),
 * and aggregation spellings folded (`avg` = `mean`). Applied to both the query
 * and the stream text so `ch1 1 hour min` finds `Bridge_CH1_60m_minimum`.
 */
export function searchKey(text: string): string {
    const words = text
        .toLowerCase()
        // `60m` / `1h` glued together: split the number from the unit.
        .replace(/(\d+)([a-z]+)/g, '$1 $2')
        .replace(/[_\-./]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')
        .filter(Boolean);
    const out: string[] = [];
    for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const next = words[i + 1];
        if (/^\d+$/.test(word) && next !== undefined && DURATION_UNIT_SECONDS[next] !== undefined) {
            out.push(`${Number(word) * DURATION_UNIT_SECONDS[next]}s`);
            i++;
            continue;
        }
        // A bare unit means one of it: `hour` → 3600s, `day` → 86400s (but not the
        // ambiguous one-letter forms on their own).
        if (word.length > 1 && DURATION_UNIT_SECONDS[word] !== undefined && !/^(sec|secs|min|mins)$/.test(word)) {
            out.push(`${DURATION_UNIT_SECONDS[word]}s`);
            continue;
        }
        out.push(AGGREGATION_SYNONYMS[word] ?? word);
    }
    return out.join(' ');
}

/**
 * Whether a stream matches a search: every word of the query occurs somewhere in
 * the stream's name, id, unit, aggregation or channel display name, in any order.
 */
export function streamMatchesSearch(stream: Datastream, query: string): boolean {
    const words = searchKey(query).split(' ').filter(Boolean);
    if (!words.length) {
        return true;
    }
    if (stream.id === query.trim()) {
        return true;
    }
    const tokens = searchKey(
        [stream.name, stream.id, stream.unit, stream.aggregation, stream.channelDisplay?.display ?? ''].join(' '),
    ).split(' ');
    return words.every((word) =>
        // A folded duration must match a whole token: `600s` is not part of `3600s`.
        /^\d+s$/.test(word) ? tokens.includes(word) : tokens.some((token) => token.includes(word)),
    );
}

/**
 * The admin-managed ignore list, as the dashboard reads it: `data.ignoreChannels`
 * on the `event-feature-extraction` /Configs row. Accepts an array, a delimited or
 * JSON string, or a name→enabled map. Returned lower-cased for matching.
 */
export const IGNORED_CHANNELS_CONFIG_NAME = 'event-feature-extraction';

export function normalizeIgnoredChannels(value: unknown): Set<string> {
    const out = new Set<string>();
    const add = (raw: unknown) => {
        const text = String(raw ?? '').trim().toLowerCase();
        if (text) {
            out.add(text);
        }
    };
    if (Array.isArray(value)) {
        value.forEach(add);
    } else if (typeof value === 'string') {
        const trimmed = value.trim();
        if ((trimmed.startsWith('[') && trimmed.endsWith(']')) || (trimmed.startsWith('{') && trimmed.endsWith('}'))) {
            try {
                return normalizeIgnoredChannels(JSON.parse(trimmed));
            } catch {
                // Fall through to delimiter parsing.
            }
        }
        trimmed.split(/[,;\n]/).forEach(add);
    } else if (value && typeof value === 'object') {
        for (const [channel, enabled] of Object.entries(value)) {
            if (enabled === false || enabled === null || enabled === 0) {
                continue;
            }
            if (typeof enabled === 'string' && /^(|false|0|off|no)$/.test(enabled.trim().toLowerCase())) {
                continue;
            }
            add(channel);
        }
    }
    return out;
}

/** Whether a stream is built on a channel the admin has told the dashboard to ignore. */
export function usesIgnoredChannel(stream: { channelNames: string[] }, ignored: Set<string>): boolean {
    return stream.channelNames.some((name) => ignored.has(name.trim().toLowerCase()));
}
